import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'

const fetchPersonData = async (levelIds) => {
  const { data } = await postNamedQuery('levels', 'search-all-people', {
    filters: { personId: [{ op: 'in', value: levelIds }] },
    includes: { personTypes: true }
  })
  return {
    data: (data?.data ?? []).reduce((acc, person) => {
      acc[person.personId] = {
        label: person.firstName + ' ' + person.lastName,
        description: person.personType?.longName,
        image: person.profilePic ?? undefined,
        ...person
      }
      return acc
    }, {}),
    levelTypeId: 200
  }
}
const fetchAccountCategoryData = async (levelIds) => {
  const { data } = await postNamedQuery('levels', 'searchGroups', {
    filters: {
      levelTypeId: [{ op: 'eq', value: 203 }],
      groupId: [{ op: 'in', value: levelIds }]
    },
    take: 9999
  })
  return {
    data: (data?.data ?? []).reduce((acc, group) => {
      acc[group.groupId] = {
        label: group.longName,
        description: group.description,
        color: group.colorField,
        ...group
      }
      return acc
    }, {}),
    levelTypeId: 203
  }
}
const fetchAccountData = async (levelIds) => {
  const { data } = await postNamedQuery('levels', 'searchAccounts', {
    filters: {
      accountId: [{ op: 'in', value: levelIds }]
    },
    take: 9999
  })
  return {
    data: (data?.data ?? []).reduce((acc, account) => {
      acc[account.accountId] = {
        label: account.accountName,
        description: account.accountNumber,
        ...account
      }
      return acc
    }, {}),
    levelTypeId: 1
  }
}
const fetchClientData = async (_clientId) => {
  const clientId = Array.isArray(_clientId) ? _clientId[0] : _clientId
  const { data } = await postNamedQuery('levels', 'get-client-by-id', {
    clientId,
    resolveAvatar: true
  })

  return {
    levelTypeId: 201,
    data: {
      [clientId]: {
        ...(data?.client.name ? { label: data.client.shortName } : {}),
        ...data?.client ?? null
      }
    }
  }
}

const fetchThingsData = async (levelIds, graphLevelTypeId, graphLevelId) => {
  const { data } = await postNamedQuery('levels', 'get-things', {
    levelTypeId: graphLevelTypeId,
    levelId: graphLevelId
  })

  return {
    levelTypeId: 279,
    data: (data?.things ?? []).reduce((acc, thing) => {
      if (levelIds.includes(thing.thingId)) {
        acc[thing.thingId] = {
          label: thing.name,
          description: thing.content,
          ...thing,
          // override the leveltypeid/levelId so that it will flow properly into items
          levelTypeId: 279,
          levelId: thing.thingId
        }
      }
      return acc
    })
  }
}

const linkedEntityDataFetchMap = {
  200: fetchPersonData,
  201: fetchClientData,
  203: fetchAccountCategoryData,
  279: fetchThingsData,
  1: fetchAccountData
}

export const fetchLinkedEntityData = async (linkedEntities, graphLevelTypeId, graphLevelId) => {
  if (!linkedEntities?.length) return {}
  const groupedEntities = linkedEntities.reduce((acc, entity) => {
    const existingLevelType = acc.find(item => item.levelTypeId === entity.levelTypeId)
    if (existingLevelType) {
      existingLevelType.levelIds = [...new Set([...existingLevelType.levelIds, entity.levelId])]
    } else {
      acc.push({ levelTypeId: entity.levelTypeId, levelIds: [entity.levelId] })
    }
    return acc
  }, [])

  const linkedEntityData = await Promise.allSettled(
    groupedEntities.map(({ levelTypeId, levelIds }) =>
      linkedEntityDataFetchMap[levelTypeId]?.(levelIds, graphLevelTypeId, graphLevelId)
    )
  )

  return linkedEntityData?.reduce((acc, { status, value }) => {
    if (status !== 'fulfilled') {
      console.error('Failed to fetch linked entity data:', value)
      return acc
    }
    if (value?.data) {
      acc[value.levelTypeId] = value.data
    }
    return acc
  }, {})
}

export const useGetLevelEntityGraphByGraphType = (props, settings) => {
  const { userId } = useAppContext()
  const { levelTypeId, levelId, graphType, includeLinkedEntityData = true } = props
  const { enabled = true, mapper } = settings || {}
  return useQuery({
    queryKey: ['get-entity-graph-by-graph-type', userId, levelTypeId, levelId, graphType, includeLinkedEntityData],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'get-entity-graph-by-graph-type', {
        levelTypeId,
        levelId,
        graphType,
        includes: {
          entityGraphTypeDetails: true
        }
      })

      if (includeLinkedEntityData) {
        data.linkedEntityData = await fetchLinkedEntityData(
          data?.data?.nodes?.map((node) => node.data?.entityProps)?.filter(Boolean),
          levelTypeId,
          levelId
        )
      }

      return data
    },
    enabled,
    select: mapper ? (data) => mapper(data) : undefined
  })
}

export const useSaveEntityGraph = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (props) => {
      await postNamedCommand('levels', 'save-entity-graph', props)
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: ['get-entity-graph-by-graph-type'], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useGetInitialLinkedData = ({ config, plugin, level, queryOptions }) => {
  const { levelId, levelTypeId } = level || {}
  return useQuery({
    queryKey: ['entity-graph-initial-linked', levelTypeId, levelId, plugin?.name],
    queryFn: async () => {
      const clientId = levelTypeId === 201 ? levelId : null
      const initial = plugin.createInitial(config, clientId)
      const linkedEntities = plugin?.getLinkedEntitiesFromNodes(initial.nodes) ?? (initial.nodes || [])
        .map((node) => node.data?.entityProps)
        .filter(Boolean)

      const linkedEntityData = linkedEntities?.length ? await fetchLinkedEntityData(linkedEntities, levelTypeId, levelId) : {}

      const slotDataArray = []
      if (plugin?.fetchSlotData) {
        try {
          for (const slotData of config?.[plugin?.key]?.defaultSlotData) {
            if (!slotData) continue
            const fetchedData = await plugin.fetchSlotData({ slotData, levelTypeId, levelId })

            if (fetchedData?.levelTypeId && fetchedData?.data) {
              slotDataArray.push(...fetchedData.data)
              const slotDataMap = fetchedData?.data?.reduce((acc, item) => {
                if (item.id) {
                  acc[item.id] = {
                    label: item.name || item.label,
                    description: item.content || item.description,
                    ...item
                  }
                }
                return acc
              }, {})

              linkedEntityData[slotData.levelTypeId] = {
                ...(linkedEntityData[slotData.levelTypeId] ?? {}),
                ...slotDataMap
              }
            }
          }
        } catch (err) {
          console.error('Error fetching slot data:', err)
        }
      }

      return { linkedEntityData, slotDataArray }
    },
    ...queryOptions
  })
}
